import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { AlertCircle, Mail, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import authService from '../../api/authService';

const OtpVerification = ({
  email,
  onVerified,
  onResend,
  onBack,
  expiresAt,
  getSessionConflictOptions,
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  // Calculate initial countdown from expiresAt if provided, otherwise default to 5 minutes (300 seconds)
  const calculateInitialCountdown = () => {
    if (expiresAt) {
      const expirationTime = new Date(expiresAt).getTime();
      const now = new Date().getTime();
      const remaining = Math.max(0, Math.floor((expirationTime - now) / 1000));
      return remaining > 0 ? remaining : 300; // Fallback to 5 minutes if expired
    }
    return 300; // 5 minutes in seconds (changed from 10 minutes)
  };
  const [countdown, setCountdown] = useState(calculateInitialCountdown());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  // Countdown timer - update every second
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      // When countdown reaches 0, disable OTP input
      setError('OTP has expired. Please request a new one.');
    }
  }, [countdown]);

  // Recalculate countdown if expiresAt prop changes (e.g., after resend)
  useEffect(() => {
    if (expiresAt) {
      const expirationTime = new Date(expiresAt).getTime();
      const now = new Date().getTime();
      const remaining = Math.max(0, Math.floor((expirationTime - now) / 1000));
      if (remaining > 0) {
        setCountdown(remaining);
      } else {
        setCountdown(0);
      }
    }
  }, [expiresAt]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (index === 5 && value) {
      const otpCode = newOtp.join('');
      if (otpCode.length === 6) {
        handleVerify(otpCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      setError('');
      // Focus last input
      inputRefs.current[5]?.focus();
      // Auto-verify
      setTimeout(() => handleVerify(pastedData), 100);
    }
  };

  const handleVerify = async (otpCode = null) => {
    const code = otpCode || otp.join('');
    
    if (code.length !== 6) {
      const errorMsg = 'Please enter 6-digit OTP';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const sessionConflictOptions =
        typeof getSessionConflictOptions === 'function' ? getSessionConflictOptions() : {};
      // Verify OTP with backend (may poll PENDING_USER_A then complete with pollToken)
      const response = await authService.verifyOtp(email, code, sessionConflictOptions);
      
      // If backend returns a token, it means:
      // 1. OTP is verified successfully
      // 2. User's password is already changed (isPasswordChanged = true)
      // 3. User can login directly without password change
      // 
      // Note: If password change is required (first-time user), backend will throw
      // AuthenticationException with "Password must be changed first" message
      if (response && response.token) {
        toast.success('OTP verified successfully!');
        // User has token, password is already changed, login directly (requiresPasswordChange = false)
        onVerified(false, code, response);
      } else {
        // No token but no error - this shouldn't happen, but handle gracefully
        throw new Error('OTP verification failed - no token received');
      }
    } catch (error) {
      if (
        error?.message === 'LOGIN_POLL_ABORTED' ||
        error?.message === 'SESSION_FLOW_TERMINATED' ||
        error?.message === 'LOGIN_REJECTED' ||
        error?.name === 'AbortError'
      ) {
        setLoading(false);
        return;
      }
      console.error('OTP verification error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Invalid OTP. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      setOtp(['', '', '', '', '', '']);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtp(['', '', '', '', '', '']);
    setError('');
    
    try {
      const response = await authService.requestOtp(email);
      // If response includes expiresAt, use it to calculate countdown
      if (response?.expiresAt) {
        const expirationTime = new Date(response.expiresAt).getTime();
        const now = new Date().getTime();
        const remaining = Math.max(0, Math.floor((expirationTime - now) / 1000));
        setCountdown(remaining > 0 ? remaining : 300);
      } else {
        setCountdown(300); // Reset to 5 minutes (changed from 10 minutes)
      }
      toast.success('New OTP sent to your email!');
      await onResend();
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to resend OTP. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="w-full max-w-md">
      <Card className="bg-card">
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl font-bold" style={{ color: '#000000' }}>
            Verify Your Email
          </CardTitle>
          <CardDescription className="text-sm" style={{ color: '#4A5B63' }}>
            We've sent a 6-digit verification code to
          </CardDescription>
          <div className="flex items-center gap-2 mt-2">
            <Mail className="w-4 h-4" style={{ color: '#006600' }} />
            <span className="font-medium" style={{ color: '#000000' }}>{email}</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* OTP Input Fields */}
          <div className="space-y-2">
            <Label className="text-sm font-medium" style={{ color: '#000000' }}>
              Enter Verification Code
            </Label>
            <div className="flex gap-2 sm:gap-2 justify-center">
              {otp.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => {
                    if (el) {
                      inputRefs.current[index] = el;
                    }
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="w-10 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-bold border-2 focus:border-[#006600] transition-all"
                  style={{
                    borderColor: error ? '#BB0000' : digit ? '#006600' : '#DADADA',
                    background: digit ? '#FFFFFF' : '#F8FCFB'
                  }}
                />
              ))}
            </div>
            {error && (
              <div className="flex items-center gap-2 text-sm text-[#BB0000]">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* Countdown Timer */}
          <div className="flex items-center justify-center gap-2 text-sm font-medium" style={{ color: countdown < 60 ? '#BB0000' : countdown < 120 ? '#FF6B00' : '#4A5B63' }}>
            <Clock className="w-4 h-4" />
            <span>
              {countdown > 0 
                ? `Code expires in ${formatTime(countdown)}`
                : 'Code has expired'
              }
            </span>
          </div>

          {/* Development Helper - Show OTP in Console */}
          {process.env.NODE_ENV === 'development' && (
            <div className="text-xs text-center" style={{ color: '#4A5B63' }}>
              <button
                type="button"
                onClick={() => {
                  const storedOtp = localStorage.getItem(`otp_${email}`);
                  if (storedOtp) {
                    //console.log(`🔐 OTP for ${email}: ${storedOtp}`);
                    toast.success('OTP logged to console (dev mode only)');
                  } else {
                    toast.error('No OTP found. Please request a new one.');
                  }
                }}
                className="underline hover:text-[#006600] transition-colors"
              >
                Show OTP in console (dev only)
              </button>
            </div>
          )}

          {/* Verify Button */}
          <Button
            onClick={() => handleVerify()}
            disabled={loading || otp.join('').length !== 6 || countdown === 0}
            className="w-full py-3 font-bold text-white"
            style={{
              background: '#006600',
              opacity: (loading || otp.join('').length !== 6 || countdown === 0) ? 0.5 : 1
            }}
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </Button>

          {/* Resend OTP */}
          <div className="text-center">
            {countdown > 0 ? (
              <p className="text-sm" style={{ color: '#4A5B63' }}>
                Didn't receive code? Resend in {formatTime(countdown)}
              </p>
            ) : (
              <button
                onClick={handleResendOtp}
                className="text-sm font-medium transition-colors"
                style={{ color: '#006600' }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                Resend Verification Code
              </button>
            )}
          </div>

          {/* Back Button */}
          <button
            onClick={onBack}
            className="w-full text-sm text-center transition-colors"
            style={{ color: '#4A5B63' }}
            onMouseEnter={(e) => e.target.style.color = '#006600'}
            onMouseLeave={(e) => e.target.style.color = '#4A5B63'}
          >
            ← Back to email entry
          </button>
        </CardContent>
      </Card>
    </div>
  );
};

export default OtpVerification;

